import { useMemo, useState } from "react";
import {
  runGa4AnalyticsAdminSource,
  updateGa4AnalyticsAdminSource,
} from "../../../../api/ga4AnalyticsSourceControls";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import {
  formatCompletedTime,
  formatRelativeTime,
} from "../../intelligence/intelligenceLabels";
import AdminBtn from "../layout/AdminBtn";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import DataSourceScheduleModal from "../dataSources/DataSourceScheduleModal";
import { mapGa4SourceToGridRow } from "../dataSources/mapDataSourceGridRow";
import Ga4AnalyticsSourceConfigureModal from "./Ga4AnalyticsSourceConfigureModal";

function LatestSyncSummary({ source }) {
  if (!source.lastSyncAt) {
    return <span className="muted">No sync yet</span>;
  }

  return (
    <div className="admin-re-latest-run">
      <div>{formatRelativeTime(source.lastSyncAt)}</div>
      <div className="muted admin-re-source-code">
        {source.lastSyncStatus || "unknown"}
        {source.daysProcessed != null ? ` · ${source.daysProcessed} day(s)` : ""}
        {source.rowsFetched != null ? ` · ${source.rowsFetched} row(s)` : ""}
      </div>
      {source.lastSyncError ? (
        <div className="muted admin-re-source-code">{source.lastSyncError}</div>
      ) : null}
      {source.lastSyncAt ? (
        <div className="muted admin-re-source-code">
          {formatCompletedTime(source.lastSyncAt)}
        </div>
      ) : null}
    </div>
  );
}

function resolveRunNowTitle(source) {
  if (!source.canConfigure) {
    return "Client View record required before GA4 sync can run.";
  }
  if (!source.propertyId) {
    return "Set the GA4 property ID in source settings before running.";
  }
  if (!source.credentialsPresent) {
    return "Configure the server GA4 service account credentials before running.";
  }
  return "Fetch GA4 reporting data now";
}

function matchesGa4StatusFilter(source, statusFilter) {
  if (!statusFilter || statusFilter === "all") {
    return true;
  }

  const health = source.health || source.healthStatus || "never_run";
  if (statusFilter === "healthy") return health === "healthy";
  if (statusFilter === "failed") return health === "failed";
  if (statusFilter === "running") return health === "running";
  if (statusFilter === "warning") return health === "warning";
  if (statusFilter === "never_run") {
    return health === "never_run" || health === "warning";
  }
  if (statusFilter === "disabled") return health === "disabled";
  return health === statusFilter;
}

function isCredentialsError(message) {
  return /service account credentials/i.test(String(message || ""));
}

export default function Ga4AnalyticsSourceControlsTable({
  sources = [],
  filters = {},
  hideEmpty = false,
  onRefresh,
  showToast,
  setSources,
}) {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const [busyKey, setBusyKey] = useState(null);
  const [configureSource, setConfigureSource] = useState(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [scheduleSource, setScheduleSource] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const filteredSources = useMemo(() => {
    let list = [...sources];
    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceKey?.toLowerCase().includes(query) ||
          source.clientViewSlug?.toLowerCase().includes(query) ||
          source.measurementId?.toLowerCase().includes(query)
      );
    }
    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.enabled || source.isActive);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.enabled && !source.isActive);
    }
    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => matchesGa4StatusFilter(source, filters.statusFilter));
    }
    return list.map((source) => mapGa4SourceToGridRow(source));
  }, [filters.enabledFilter, filters.search, filters.statusFilter, sources]);

  const handleToggleEnabled = async (source) => {
    if (!canManage) return;
    const nextEnabled = !source.enabled;
    setBusyKey(source.sourceKey);
    try {
      const body = await updateGa4AnalyticsAdminSource(source.sourceKey, { enabled: nextEnabled });
      showToast?.(
        nextEnabled ? `${source.sourceName} enabled.` : `${source.sourceName} disabled.`,
        "success"
      );
      setSources?.((current) =>
        current.map((row) =>
          row.sourceKey === source.sourceKey ? body.source || { ...row, enabled: nextEnabled } : row
        )
      );
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Unable to update Google Analytics source.", "error");
    } finally {
      setBusyKey(null);
    }
  };

  const handleRunNow = async (source) => {
    if (!canManage) return;

    if (!source.credentialsPresent) {
      setConfigureSource(source);
      showToast?.(
        "Configure the server GA4 service account credentials before running sync.",
        "error"
      );
      return;
    }

    setBusyKey(source.sourceKey);
    setSources?.((current) =>
      current.map((row) =>
        row.sourceKey === source.sourceKey ? { ...row, isRunning: true } : row
      )
    );
    try {
      const body = await runGa4AnalyticsAdminSource(source.sourceKey);
      showToast?.(
        `GA4 sync completed for ${source.sourceName}: ${body.syncRun?.daysProcessed || 0} day(s)`,
        "success"
      );
      setSources?.((current) =>
        current.map((row) =>
          row.sourceKey === source.sourceKey
            ? {
                ...row,
                health: "healthy",
                healthStatus: "healthy",
                lastSyncAt: body.syncRun?.completedAt || new Date().toISOString(),
                lastSyncStatus: "completed",
                daysProcessed: body.syncRun?.daysProcessed,
                rowsFetched: body.syncRun?.rowsFetched,
                isRunning: false,
              }
            : row
        )
      );
      await onRefresh?.({ silent: true });
    } catch (error) {
      if (isCredentialsError(error.message)) {
        setConfigureSource(source);
      }
      showToast?.(error.message || "GA4 sync failed.", "error");
      setSources?.((current) =>
        current.map((row) =>
          row.sourceKey === source.sourceKey ? { ...row, isRunning: false } : row
        )
      );
    } finally {
      setBusyKey(null);
    }
  };

  const handleSaveConfig = async (source, payload) => {
    if (!canManage) return;
    setIsSavingConfig(true);
    try {
      const body = await updateGa4AnalyticsAdminSource(source.sourceKey, payload);
      showToast?.(`Saved GA4 settings for ${source.sourceName}.`, "success");
      setSources?.((current) =>
        current.map((row) => (row.sourceKey === source.sourceKey ? body.source : row))
      );
      setConfigureSource(null);
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Failed to save GA4 configuration.", "error");
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleSaveSchedule = async (source, payload) => {
    if (!canManage) return;
    setScheduleSaving(true);
    try {
      const body = await updateGa4AnalyticsAdminSource(source.sourceKey, {
        enabled: payload.scheduleEnabled === true,
        pollIntervalMinutes: payload.scheduleIntervalMinutes || source.pollIntervalMinutes || 60,
      });
      showToast?.(`Schedule updated for ${source.sourceName}.`, "success");
      setSources?.((current) =>
        current.map((row) => (row.sourceKey === source.sourceKey ? body.source : row))
      );
      setScheduleSource(null);
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Failed to save GA4 schedule.", "error");
      throw error;
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleCredentialsSaved = async () => {
    showToast?.("GA4 service account credentials saved.", "success");
    setSources?.((current) =>
      current.map((row) => ({
        ...row,
        credentialsPresent: true,
      }))
    );
    await onRefresh?.({ silent: true });
  };

  if (hideEmpty && !filteredSources.length) {
    return null;
  }

  if (sources.length === 0) {
    return (
      <p className="muted">
        Google Analytics sources could not be loaded. Run server migrations and refresh this page.
      </p>
    );
  }

  if (!filteredSources.length) {
    return (
      <p className="muted">No Google Analytics sources match the current filters.</p>
    );
  }

  return (
    <>
      <DataSourceControlsGrid
        sources={filteredSources}
        emptyMessage="No Google Analytics sources match the current filters."
        busyKey={busyKey}
        canManageSources={canManage}
        onToggleEnabled={handleToggleEnabled}
        onRunNow={handleRunNow}
        onSaveSchedule={handleSaveSchedule}
        onViewRuns={(source) => setConfigureSource(source)}
        viewRunsLabel="Connection settings"
        sectionLabel="Analytics"
        deleteDomain="analytics"
        onDeleted={() => onRefresh?.()}
        resolveRunNowTitle={resolveRunNowTitle}
        extraActions={(source) =>
          canManage ? (
            <AdminBtn variant="secondary" onClick={() => setConfigureSource(source)}>
              Configure
            </AdminBtn>
          ) : null
        }
        renderSourceExtras={(source) => <LatestSyncSummary source={source} />}
      />
      <DataSourceScheduleModal
        source={scheduleSource}
        isOpen={Boolean(scheduleSource)}
        isSaving={scheduleSaving}
        onClose={() => setScheduleSource(null)}
        onSave={handleSaveSchedule}
      />

      <Ga4AnalyticsSourceConfigureModal
        source={configureSource}
        isOpen={Boolean(configureSource)}
        isSaving={isSavingConfig}
        onClose={() => setConfigureSource(null)}
        onSave={handleSaveConfig}
        onCredentialsSaved={handleCredentialsSaved}
      />
    </>
  );
}
